import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import Select, { type SelectChangeEvent } from '@mui/material/Select'
import {
  choiceDraftFromLegacy,
  toggleSingleSelectId,
} from '../../../lib/choice-answer'
import { resolveDisplayOptions } from '../../../lib/survey-option-display'
import type { ChoiceDraftAnswer, SurveyDraftAnswer, SurveyQuestion } from '../../../lib/survey-question.types'
import {
  surveyDropdownFieldSx,
  surveyDropdownMenuPaperSx,
  surveyDropdownPlaceholderSx,
  surveyDropdownValueSx,
} from '../../../styles/surveyExecutionStyles'
import { ChoiceTextSpecify } from './OtherSpecifyField'

interface DropdownQuestionProps {
  readonly question: SurveyQuestion
  readonly value: SurveyDraftAnswer
  readonly displaySeed: string
  readonly onChange: (value: ChoiceDraftAnswer) => void
}

function dropdownPlaceholderLabel(question: SurveyQuestion): string {
  return question.placeholder ?? 'Select an option'
}

export function DropdownQuestion({
  question,
  value,
  displaySeed,
  onChange,
}: DropdownQuestionProps) {
  const draft = choiceDraftFromLegacy(question, value)
  const options = resolveDisplayOptions(question, displaySeed)
  const selectedId = draft.selectedIds[0] ?? ''

  const handleChange = (event: SelectChangeEvent<string>) => {
    const next = event.target.value
    if (!next) return
    const nextIds = toggleSingleSelectId(draft.selectedIds, next)
    onChange({ selectedIds: nextIds, otherText: draft.otherText })
  }

  const placeholder = dropdownPlaceholderLabel(question)

  return (
    <Box>
      <FormControl fullWidth sx={surveyDropdownFieldSx}>
        <Select
          displayEmpty
          value={selectedId}
          onChange={handleChange}
          data-testid={`survey-q-${question.id}-dropdown`}
          inputProps={{ 'aria-label': question.prompt }}
          MenuProps={{
            PaperProps: { sx: surveyDropdownMenuPaperSx },
          }}
          renderValue={(sel) => {
            if (!sel) {
              return (
                <Box component="span" sx={surveyDropdownPlaceholderSx}>
                  {placeholder}
                </Box>
              )
            }
            const opt = options.find((o) => o.id === sel)
            return (
              <Box component="span" sx={surveyDropdownValueSx}>
                {opt?.label ?? sel}
              </Box>
            )
          }}
        >
          {options.map((opt) => (
            <MenuItem
              key={opt.id}
              value={opt.id}
              data-testid={`survey-q-${question.id}-option-${opt.id}`}
            >
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <ChoiceTextSpecify question={question} draft={draft} onChange={onChange} />
    </Box>
  )
}
